import React, { FC } from "react";

import { Translate } from "@/app/application/Translate";
import { TrainingLinkRequest } from "@/features/training/model/Training/TrainingLink";
import { CustomCloseButton } from "@/features/training/ui";
import { CustomCloseIcon } from "@/features/training/ui";
import { CustomDeleteButton } from "@/features/training/ui";
import { CustomSaveButton } from "@/features/training/ui";
import CustomInput from "@/shared/forms/CustomInput";
import CustomSelect from "@/shared/forms/CustomSelect";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import { AlertToast } from "@/shared/notifications/AlertToast";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	InputAdornment,
	LinearProgress,
	Stack,
	Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import {
	useMutateDeleteTrainingLink,
	useMutateUpdateTrainingLink,
} from "@/service/TrainingMutations";
import {
	useFetchCourseFolders,
	useFetchTrainingLink,
} from "@/service/TrainingQueries";

interface EditTrainingLinkProps {
	handleClose: () => void;
	open: boolean;
	linkId: number;
}

type LinkFormValues = {
	Name: string;
	UrlPath: string;
	FolderId: string | number | null;
};

const validationSchema = Yup.object().shape({
	Name: Yup.string().trim().required("Name is required"),
	UrlPath: Yup.string().trim().required("Url is required"),
	// FolderId is optional; “General” represents no folder
});

export const EditTrainingLink: FC<EditTrainingLinkProps> = (props) => {
	const [isDeleting, setIsDeleting] = React.useState(false);
	const { isLoading: isLoadingFolders, data: folders } =
		useFetchCourseFolders();
	const { isLoading: isLoadingLink, data: link } = useFetchTrainingLink(
		props.linkId,
	);

	const updateTrainingLink = useMutateUpdateTrainingLink();
	const deleteTrainingLink = useMutateDeleteTrainingLink();

	const handleSave = async (values: LinkFormValues) => {
		const request: TrainingLinkRequest = {
			Name: values.Name,
			UrlPath: values.UrlPath,
			FolderId:
				values.FolderId === "General" || values.FolderId == null
					? 0
					: Number(values.FolderId),
		};

		try {
			await updateTrainingLink({ request, linkId: props.linkId });
			props.handleClose();
			void AlertToast.success();
		} catch (error) {
			console.error(error);
			props.handleClose();
			void AlertToast.failed();
		}
	};

	const handleDelete = async () => {
		if (isDeleting) {
			return;
		}

		try {
			setIsDeleting(true);
			props.handleClose();
			const confirmation = await AlertRequest.delete();
			if (!confirmation.isConfirmed) return;
			await deleteTrainingLink(link.Id);
			AlertRequest.deleteConfirmed(link.Name);
		} catch (error) {
			console.error(error);
			void AlertRequest.failed();
		} finally {
			setIsDeleting(false);
		}
	};

	if (isLoadingFolders || isLoadingLink || !folders || !link) {
		return (
			<Box
				sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
			>
				<LinearProgress />
			</Box>
		);
	}

	// Normalize initial folder value: if there’s no folder, show “General”
	const initialFolder =
		link.FolderId && Number(link.FolderId) > 0
			? String(link.FolderId)
			: "General";

	return (
		<Dialog
			onClose={props.handleClose}
			open={props.open}
			fullWidth
			maxWidth="xs"
			aria-labelledby="edit-link-title"
			PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
		>
			<DialogTitle
				id="edit-link-title"
				sx={{ pr: 6, pb: 0.5, fontWeight: 700 }}
			>
				<Translate String="Training.EditLink.Title">Edit Link</Translate>
			</DialogTitle>

			<CustomCloseIcon onClick={props.handleClose} />

			<DialogContent dividers sx={{ pt: 1.5 }}>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					<Translate String="Training.EditLink.Subtitle">
						Update the link details or move it into a folder.
					</Translate>
				</Typography>

				<Formik<LinkFormValues>
					initialValues={{
						Name: link.Name ?? "",
						UrlPath: link.UrlPath ?? "",
						FolderId: initialFolder,
					}}
					validationSchema={validationSchema}
					validateOnMount
					onSubmit={handleSave}
				>
					{({ isValid, dirty, isSubmitting }) => (
						<Form>
							<Stack spacing={2}>
								<CustomInput
									id="Name"
									type="text"
									label="Name"
									name="Name"
									translationString="Training.EditLink.Name"
									placeholder="e.g. Safety Handbook"
									autoFocus
									fullWidth
									required
									onKeyDown={(e) => e.stopPropagation()}
								/>

								<CustomInput
									id="UrlPath"
									type="text"
									label="Url"
									name="UrlPath"
									required
									translationString="Training.EditLink.Url"
									placeholder="https://example.com/resource"
									fullWidth
									onKeyDown={(e) => e.stopPropagation()}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<LinkOutlinedIcon fontSize="small" />
											</InputAdornment>
										),
									}}
								/>

								<Box>
									<CustomSelect
										label="Folder"
										name="FolderId"
										id="FolderId"
										translationString="Training.EditLink.Folder"
										fullWidth
									>
										<option value="General">
											<Translate String="Training.EditLink.Folder.General">
												General (no folder)
											</Translate>
										</option>
										{folders.map((x) => (
											<option value={x.Id} key={x.Id}>
												{x.Name}
											</option>
										))}
									</CustomSelect>
									<Typography variant="caption" color="text.secondary">
										<Translate String="Training.EditLink.Folder.Help">
											Choose a folder, or keep “General” to store this link
											without a folder.
										</Translate>
									</Typography>
								</Box>

								<DialogActions
									sx={{
										px: 0,
										pt: 2,
										mt: 0.5,
										borderTop: (t) => `1px solid ${t.palette.divider}`,
										display: "flex",
										justifyContent: "space-between",
										gap: 1,
									}}
								>
									<CustomDeleteButton
										handleDelete={handleDelete}
										loading={isDeleting}
										disabled={isSubmitting || isDeleting}
										textOverride={
											<Translate String="Training.EditLink.DeleteButton">
												Delete link
											</Translate>
										}
									/>

									<Box sx={{ display: "flex", gap: 1 }}>
										<CustomCloseButton
											handleClose={props.handleClose}
											disabled={isSubmitting || isDeleting}
										/>
										<CustomSaveButton
											loading={isSubmitting}
											disabled={
												!isValid || !dirty || isSubmitting || isDeleting
											}
										/>
									</Box>
								</DialogActions>
							</Stack>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};
